import type { DashboardSaveV2Request, VehicleSectionV2 } from "@credit-acceptance/step6-bff-client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useState } from "react";
import { useController, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Form } from "../../../../../components/ui/form";
import { LabeledValue } from "../../../../../components/ui/labeledValue";
import { SubsectionHeader } from "../../../../../components/ui/subsectionHeader";
import { formatCurrencyForDisplay } from "../../../../../components/ui/uikit/currencyInput";
import { getErrorMessage } from "../../../../../lib/query/errors";
import { useRegisterSaveableSection } from "../../../useRegisterSaveableSection";
import {
  buildContractVehicleDefaultValues,
  contractVehicleSchema,
  isNewVehicle,
  LIC_PLATE_MAX_LENGTH,
  ODOMETER_DISC_MILEAGE_MAX_LENGTH,
  TOTAL_SELLING_PRICE_MAX_LENGTH,
} from "./ContractVehicleInformation.schema";
import { CheckboxField, CurrencyField, DateField, TextField } from "./Fields";

const BOOK_SOURCE_LABEL = "kelly blue book";
const BOOK_VALUE_PLACEHOLDER = "NA";

// `== null` rather than `=== undefined`: VehicleOnDealV2's optionals are
// serialized as explicit JSON nulls by the BFF ("null when ..." in its own
// field docs), so both absences have to collapse to the same placeholder.
function formatBookValue(value?: number | null): string {
  return value == null ? BOOK_VALUE_PLACEHOLDER : formatCurrencyForDisplay(value);
}

interface ContractVehicleInformationProperties {
  /** V2 dashboard vehicle section: `vehicleOnDeal` drives the deal data, `licensePlateNo` sits on the section itself. */
  readonly vehicle?: VehicleSectionV2;
  /** `financial.vehicle.condition`; Total Selling Price is editable only when this is NEW. */
  readonly condition?: string;
  /**
   * Whether Odometer Disc Mileage and CPO Date are typeable (Total Selling Price additionally needs a NEW
   * vehicle). Defaults to `false` so the fields fail safe to read-only until a caller resolves the VLA-lock,
   * role, deal-state and pre-approval gates — same "defaults to disabled" convention as `editable`.
   */
  readonly contractVehicleEditable?: boolean;
  /** Whether Lic Plate # is typeable — not in the ticket's editable list, so it keeps the section-wide gate; defaults to disabled. */
  readonly editable?: boolean;
}

/**
 * Contract Vehicle subsection, seeded from the V2 dashboard `vehicle.vehicleOnDeal`. Year/Make/Model/VIN/Color
 * and Book Wholesale/Retail are read-only `LabeledValue`s; Odometer Disc Mileage, Total Selling Price (NEW only),
 * and CPO Date are RHF + Zod fields on `Fields.tsx`'s dual-mode inputs. Certified Pre-Owned is never
 * user-editable and only controls CPO Date's visibility; CPO Date is required while visible
 * (see `contractVehicleSchema`'s `superRefine`).
 *
 * Registered with the shared save orchestrator via `useRegisterSaveableSection`, but this is plumbing
 * only: `DashboardSaveV2Request.vehicle` is documented "Not writable today — accepted for round-trip
 * convenience and ignored," so edits reach the PUT body and the BFF discards them.
 */
export function ContractVehicleInformation({
  vehicle,
  condition,
  contractVehicleEditable = false,
  editable = false,
}: ContractVehicleInformationProperties) {
  const vehicleOnDeal = vehicle?.vehicleOnDeal;
  // @TODO: Partial rule — spec §5.2 is `!disableMsrp && !preAppDeal && !readOnly`, where `disableMsrp` means
  // a NEW vehicle in an MTDREBATE state. The MTDREBATE-state input isn't exposed by the BFF yet, so this
  // only covers the NEW-vehicle half; the pre-approval half rides on `contractVehicleEditable` from the caller.
  const totalSellingPriceEditable = contractVehicleEditable && isNewVehicle(condition);
  const { t } = useTranslation();
  const form = useForm({
    resolver: zodResolver(contractVehicleSchema),
    mode: "onBlur",
    defaultValues: buildContractVehicleDefaultValues(vehicle),
  });
  const { control } = form;
  const odometerDiscMileage = useController({ control, name: "odometerDiscMileage" });
  const licPlate = useController({ control, name: "licPlate" });
  const totalSellingPrice = useController({ control, name: "totalSellingPrice" });
  const isCertifiedPreOwned = useController({ control, name: "isCertifiedPreOwned" });
  const cpoDate = useController({ control, name: "cpoDate" });

  // TODO: wire to the real odometer disclosure link/modal once the destination is decided.
  const handleOdometerDiscMileageLabelClick = useCallback(() => {}, []);

  // CPO Date validates on change rather than blur (unlike the other fields
  // here) — its future-date check needs to surface as soon as a value lands,
  // not after an extra blur step. `trigger` re-runs the whole schema's
  // superRefine but only surfaces the result for this field.
  function handleCpoDateChange(value: string) {
    cpoDate.field.onChange(value);
    void form.trigger("cpoDate");
  }

  // Same as Signers' own save-time (as opposed to live-validation) error —
  // only set when the shared save call itself fails.
  const [saveError, setSaveError] = useState<string>();

  useRegisterSaveableSection("contractVehicle", {
    validate: () => form.trigger(),
    getPayload: (): Partial<DashboardSaveV2Request> => ({ vehicle: form.getValues() }),
    onSaved: () => {
      setSaveError(undefined);
      // Re-baselines RHF's dirty tracking to the just-"saved" values, same as
      // Signers' onSaved — see Signers.tsx's onSaved for why (keepValues
      // leaves the displayed values alone; only isDirty/dirtyFields clear).
      form.reset(undefined, { keepValues: true, keepDirty: false, keepErrors: true });
    },
    onSaveFailed: (error) => {
      setSaveError(getErrorMessage(error));
    },
  });

  return (
    <div>
      <SubsectionHeader>{t("vehicle.contractVehicle.heading")}</SubsectionHeader>
      {saveError && (
        <p role="alert" className="text-destructive text-2xs px-3 pt-2">
          {saveError}
        </p>
      )}
      <Form {...form}>
        <form onSubmit={(event) => event.preventDefault()} noValidate>
          <div className="gap-(--spacing-3) px-(--spacing-3) py-(--spacing-3) flex flex-col">
            <div className="gap-(--spacing-4) grid grid-cols-2 sm:grid-cols-3">
              <LabeledValue label={t("vehicle.contractVehicle.year")} value={vehicleOnDeal?.year} />
              <LabeledValue label={t("vehicle.contractVehicle.make")} value={vehicleOnDeal?.make} />
              <LabeledValue label={t("vehicle.contractVehicle.model")} value={vehicleOnDeal?.model} />
              <LabeledValue
                label={t("vehicle.contractVehicle.vin")}
                value={vehicleOnDeal?.vehicleIdentificationNumber}
              />
              <LabeledValue label={t("vehicle.contractVehicle.color")} value={vehicleOnDeal?.color} />
            </div>

            <div className="gap-(--spacing-4) mt-(--spacing-3) grid grid-cols-2 sm:grid-cols-3">
              {/* @TODO: Replace the hardcoded KBB sub-label once the backend exposes the book source */}
              <LabeledValue
                label={t("vehicle.contractVehicle.bookWholesale")}
                value={formatBookValue(vehicleOnDeal?.adjustedWholesaleValue)}
                subLabel={vehicleOnDeal?.adjustedWholesaleValue == null ? undefined : BOOK_SOURCE_LABEL}
              />
              <LabeledValue
                label={t("vehicle.contractVehicle.bookRetail")}
                value={formatBookValue(vehicleOnDeal?.adjustedRetailValue)}
                subLabel={vehicleOnDeal?.adjustedRetailValue == null ? undefined : BOOK_SOURCE_LABEL}
              />
            </div>

            <div className="gap-(--spacing-4) grid grid-cols-2">
              <TextField
                label={t("vehicle.contractVehicle.odometerDiscMileage")}
                value={odometerDiscMileage.field.value}
                onChange={odometerDiscMileage.field.onChange}
                onBlur={odometerDiscMileage.field.onBlur}
                error={odometerDiscMileage.fieldState.error}
                disabled={!contractVehicleEditable}
                maxLength={ODOMETER_DISC_MILEAGE_MAX_LENGTH}
                onLabelClick={handleOdometerDiscMileageLabelClick}
              />

              <TextField
                label={t("vehicle.contractVehicle.licPlate")}
                value={licPlate.field.value}
                onChange={licPlate.field.onChange}
                onBlur={licPlate.field.onBlur}
                error={licPlate.fieldState.error}
                disabled={!editable}
                maxLength={LIC_PLATE_MAX_LENGTH}
                uppercase
              />
              <CurrencyField
                label={t("vehicle.contractVehicle.totalSellingPrice")}
                value={totalSellingPrice.field.value}
                onChange={totalSellingPrice.field.onChange}
                onBlur={totalSellingPrice.field.onBlur}
                error={totalSellingPrice.fieldState.error}
                disabled={!totalSellingPriceEditable}
                maxLength={TOTAL_SELLING_PRICE_MAX_LENGTH}
              />
              {isCertifiedPreOwned.field.value && (
                <div className="gap-(--spacing-2) flex flex-col">
                  <DateField
                    label={t("vehicle.contractVehicle.cpoDate")}
                    value={cpoDate.field.value}
                    onChange={handleCpoDateChange}
                    onBlur={cpoDate.field.onBlur}
                    error={cpoDate.fieldState.error}
                    disabled={!contractVehicleEditable}
                  />
                  <CheckboxField
                    label={t("vehicle.contractVehicle.certifiedPreOwned")}
                    checked={isCertifiedPreOwned.field.value}
                    disabled
                  />
                </div>
              )}
            </div>
          </div>
        </form>
      </Form>
    </div>
  );
}
